import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { 
  ArrowLeft, Globe, Upload, FileText, Loader2, CheckCircle, AlertCircle, ArrowRight, ChevronDown, Clock, X
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import PageHeader from './PageHeader';
import SEO from './SEO';
import { API_BASE_URL } from '../config/api';

const URL_HISTORY_KEY = 'pathnav_url_history';
const MAX_URL_HISTORY = 5;

const SitemapImporter = () => {
  const navigate = useNavigate();
  
  // Input mode: 'url', 'upload', 'paste'
  const [inputMode, setInputMode] = useState('url');
  
  // Form state
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [pastedContent, setPastedContent] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  
  // URL history state
  const [urlHistory, setUrlHistory] = useState([]);
  const [showUrlDropdown, setShowUrlDropdown] = useState(false);
  
  // Processing state
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, status: '' });
  
  // Results
  const [validationResult, setValidationResult] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  
  // Options
  const [includeH1, setIncludeH1] = useState(true);
  const [includeH2, setIncludeH2] = useState(false);
  const [isFetchingHeadlines, setIsFetchingHeadlines] = useState(false);

  // Load URL history from localStorage on mount
  useEffect(() => {
    try {
      const savedHistory = localStorage.getItem(URL_HISTORY_KEY);
      if (savedHistory) {
        const history = JSON.parse(savedHistory);
        setUrlHistory(history);
        // Set the most recent URL as the default
        if (history.length > 0) {
          setWebsiteUrl(history[0]);
        }
      }
    } catch (e) {
      console.error('Error loading URL history:', e);
    }
  }, []);

  // Save URL to history
  const saveUrlToHistory = (url) => {
    if (!url || !url.trim()) return;
    
    const normalizedUrl = url.trim().toLowerCase();
    
    // Remove if already exists (to move to front)
    const filteredHistory = urlHistory.filter(
      u => u.toLowerCase() !== normalizedUrl
    );
    
    // Add to front, keep only last MAX_URL_HISTORY
    const newHistory = [url.trim(), ...filteredHistory].slice(0, MAX_URL_HISTORY);
    
    setUrlHistory(newHistory);
    localStorage.setItem(URL_HISTORY_KEY, JSON.stringify(newHistory));
  };

  // Remove URL from history
  const removeUrlFromHistory = (urlToRemove, e) => {
    e.stopPropagation();
    const newHistory = urlHistory.filter(u => u !== urlToRemove);
    setUrlHistory(newHistory);
    localStorage.setItem(URL_HISTORY_KEY, JSON.stringify(newHistory));
  };

  // Parse sitemap XML content (returns URLs with content type)
  const parseSitemapXml = (xmlContent) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlContent, 'text/xml');
    
    // Check for parse errors
    const parseError = doc.querySelector('parsererror');
    if (parseError) {
      throw new Error('Invalid XML format');
    }
    
    // Extract URLs with type annotations
    const urlData = [];
    const urlElements = doc.querySelectorAll('url');
    urlElements.forEach(el => {
      const loc = el.querySelector('loc');
      if (loc) {
        const url = loc.textContent.trim();
        // Get data-type attribute if present (from our backend annotation)
        const contentType = el.getAttribute('data-type') || 'page';
        urlData.push({ url, contentType });
      }
    });
    
    // Also check for sitemap index
    const sitemapElements = doc.querySelectorAll('sitemap loc');
    if (sitemapElements.length > 0 && urlData.length === 0) {
      throw new Error('This is a sitemap index. Please provide a direct sitemap URL.');
    }
    
    if (urlData.length === 0) {
      throw new Error('No URLs found in sitemap');
    }
    
    return urlData;
  };

  // Build hierarchy from URLs (now with content type from sitemap parsing)
  const buildHierarchy = (urlDataArray) => {
    const nodes = [];
    const nodeMap = new Map();
    
    // Parse all URLs and create nodes
    urlDataArray.forEach((urlData, index) => {
      try {
        // urlData is now { url, contentType }
        const url = typeof urlData === 'string' ? urlData : urlData.url;
        const contentType = typeof urlData === 'string' ? 'page' : (urlData.contentType || 'page');
        
        const urlObj = new URL(url);
        const path = urlObj.pathname;
        const pathParts = path.split('/').filter(p => p);
        
        const node = {
          id: `node-${index}`,
          url: url,
          path: path || '/',
          name: pathParts.length > 0 ? pathParts[pathParts.length - 1] : 'Home',
          depth: pathParts.length,
          contentType: contentType, // From sitemap annotation
          children: []
        };
        
        nodes.push(node);
        nodeMap.set(path || '/', node);
      } catch (e) {
        console.warn('Invalid URL:', urlData);
      }
    });
    
    // Sort by depth then path
    nodes.sort((a, b) => a.depth - b.depth || a.path.localeCompare(b.path));
    
    return { nodes, total: urlDataArray.length };
  };

  // Detect content type from URL patterns (fallback when sitemap doesn't provide type)
  const detectContentType = (url) => {
    const path = new URL(url).pathname.toLowerCase();
    if (path.includes('/tag/') || path.includes('/tags/')) return 'tag';
    if (path.includes('/category/') || path.includes('/categories/')) return 'category';
    if (path.includes('/author/')) return 'author';
    // WordPress blog post patterns (contains date-like segments or /blog/)
    if (path.includes('/blog/') || /\/\d{4}\/\d{2}\//.test(path)) return 'post';
    // Common blog post indicators in path
    if (path.includes('/post/') || path.includes('/article/') || path.includes('/news/')) return 'post';
    return 'page';
  };

  // Sanitize passage name for Twee (remove special characters)
  const sanitizePassageName = (name, suffix = '') => {
    const clean = name
      .replace(/[[\]{}|]/g, '') // Remove Twee special chars
      .replace(/\s+/g, ' ')     // Normalize whitespace
      .trim()
      .substring(0, 50);        // Limit length
    return suffix ? `${clean}_${suffix}` : clean;
  };

  // Track used passage names to avoid duplicates
  let usedPassageNames = new Set();
  
  const getUniquePassageName = (baseName) => {
    let name = baseName;
    let counter = 1;
    while (usedPassageNames.has(name)) {
      name = `${baseName}_${counter}`;
      counter++;
    }
    usedPassageNames.add(name);
    return name;
  };

  // Convert to Twee format for compiler
  // HIERARCHICAL STRUCTURE:
  // - Home (Start) has: direct link, H1, H2s, and other pages as children
  // - Each page has: its own direct link, H1, H2s as children
  const convertToTwee = (nodes, siteTitle, headlineData = {}, options = {}) => {
    const { includeH1Nodes = false, includeH2Nodes = false } = options;
    
    // Reset passage tracking
    usedPassageNames = new Set();
    usedPassageNames.add('Start'); // Reserve Start
    
    let twee = `:: StoryTitle\n${siteTitle || 'Site Navigation'}\n\n`;
    
    // Store all passages to write at the end
    const allPassages = [];
    
    // Categorize nodes by content type
    const groups = {
      page: [],
      post: [],
      tag: [],
      category: [],
      author: [],
      product: []
    };
    
    nodes.forEach(node => {
      let contentType = node.contentType || detectContentType(node.url);
      if (!groups[contentType]) contentType = 'page';
      groups[contentType].push(node);
    });
    
    // Find the homepage (depth 0 or path === '/')
    const homepage = groups.page.find(n => n.depth === 0 || n.path === '/');
    const otherPages = groups.page.filter(n => n !== homepage);
    
    // Helper to create a page with its children (H1s, H2s - NO separate link nodes)
    // Each node has its URL stored in metadata, displayed via URL icon in Visual Editor
    const createPageWithChildren = (pageNode, pagePassageId, isHomepage = false) => {
      const headlines = headlineData[pageNode.url];
      
      // For homepage, use "Home" as the display name
      // For other pages, use H1 or title
      const pageName = isHomepage 
        ? 'Home'
        : (headlines?.h1?.text || headlines?.title || 
           pageNode.name.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
      
      // Start building this page's passage content
      let passageContent = '';
      passageContent += `<!--@url: ${pageNode.url}-->\n`;
      passageContent += `<!--@nodeLevel: page-->\n`;
      passageContent += `<!--@nodeStyle: standard-->\n`;
      passageContent += `<!--@displayName: ${pageName}-->\n`;
      passageContent += `\n`; // Empty body (description dropdown blank)
      
      // Children of this page (H1s and H2s only, no separate link nodes)
      const childLinks = [];
      
      // H1 for this page (if enabled) - skip if H1 text matches page name to avoid duplicates
      if (includeH1Nodes && headlines?.h1) {
        const h1Text = headlines.h1.text;
        // Only create H1 node if it's different from the page name
        if (h1Text !== pageName) {
          const h1Id = getUniquePassageName(sanitizePassageName(h1Text));
          childLinks.push(`[[${h1Text}|${h1Id}]]`);
          allPassages.push({
            id: h1Id,
            content: `<!--@url: ${headlines.h1.url}-->\n<!--@nodeLevel: h1-->\n<!--@nodeStyle: light-->\n<!--@displayName: ${h1Text}-->\n\n[[Back|${pagePassageId}]]`
          });
        }
      }
      
      // H2s for this page (if enabled) - light style
      if (includeH2Nodes && headlines?.h2s && headlines.h2s.length > 0) {
        headlines.h2s.forEach((h2) => {
          const h2Id = getUniquePassageName(sanitizePassageName(h2.text));
          childLinks.push(`[[${h2.text}|${h2Id}]]`);
          allPassages.push({
            id: h2Id,
            content: `<!--@url: ${h2.url}-->\n<!--@nodeLevel: h2-->\n<!--@nodeStyle: light-->\n<!--@displayName: ${h2.text}-->\n\n[[Back|${pagePassageId}]]`
          });
        });
      }
      
      // Add child links to passage content
      passageContent += childLinks.join('\n');
      if (childLinks.length > 0) passageContent += '\n';
      
      // Add back link if not homepage
      if (!isHomepage) {
        passageContent += `\n[[Back to Home|Start]]`;
      }
      
      return { passageContent, pageName };
    };
    
    // Build Start passage (homepage becomes Start)
    if (homepage) {
      const { passageContent: homeContent, pageName: homeName } = createPageWithChildren(homepage, 'Start', true);
      
      // Start passage header + home content
      twee += `:: Start\n`;
      twee += homeContent;
      
      // Add other pages as children of Start
      if (otherPages.length > 0) {
        twee += '\n';
        otherPages.forEach((node) => {
          const pageHeadlines = headlineData[node.url];
          const pageName = pageHeadlines?.h1?.text || pageHeadlines?.title || 
            node.name.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          const pageId = getUniquePassageName(sanitizePassageName(pageName));
          
          twee += `[[${pageName}|${pageId}]]\n`;
          
          // Create this page with its children
          const { passageContent } = createPageWithChildren(node, pageId, false);
          allPassages.push({
            id: pageId,
            content: passageContent
          });
        });
      }
      
      // Add section links for grouped content
      if (groups.post.length > 0) {
        twee += `\n[[Blog Posts (${groups.post.length})|_Blog_Posts]]`;
      }
      if (groups.tag.length > 0) {
        twee += `\n[[Tags (${groups.tag.length})|_Tags]]`;
      }
      if (groups.category.length > 0) {
        twee += `\n[[Categories (${groups.category.length})|_Categories]]`;
      }
      
      twee += '\n\n';
      
    } else {
      // Fallback if no homepage found
      twee += `:: Start\n`;
      twee += `<!--@url: [root]-->\n`;
      twee += `<!--@nodeLevel: root-->\n`;
      twee += `<!--@displayName: ${siteTitle || 'Home'}-->\n`;
      twee += `\n`;
      
      groups.page.forEach((node) => {
        const headlines = headlineData[node.url];
        const linkName = headlines?.h1?.text || headlines?.title || 
          node.name.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        const passageId = getUniquePassageName(sanitizePassageName(linkName));
        twee += `[[${linkName}|${passageId}]]\n`;
        
        const { passageContent } = createPageWithChildren(node, passageId, false);
        allPassages.push({
          id: passageId,
          content: passageContent
        });
      });
      twee += '\n';
    }
    
    // Write all child passages
    allPassages.forEach(passage => {
      twee += `:: ${passage.id}\n`;
      twee += `${passage.content}\n\n`;
    });
    
    // Create Blog Posts section
    if (groups.post.length > 0) {
      twee += `:: _Blog_Posts\n`;
      twee += `<!--@url: [blog-index]-->\n`;
      twee += `<!--@nodeLevel: section-->\n`;
      twee += `<!--@displayName: Blog Posts-->\n`;
      twee += `\n`;
      groups.post.forEach((node) => {
        const headlines = headlineData[node.url];
        const linkName = headlines?.h1?.text || headlines?.title || 
          node.name.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        const postId = getUniquePassageName(sanitizePassageName(linkName));
        twee += `[[${linkName}|${postId}]]\n`;
        
        const { passageContent } = createPageWithChildren(node, postId, false);
        // Modify back link to go to Blog Posts
        const modifiedContent = passageContent.replace('[[Back to Home|Start]]', '[[Back to Blog Posts|_Blog_Posts]]');
        allPassages.push({
          id: postId,
          content: modifiedContent
        });
      });
      twee += `[[Back to Home|Start]]\n\n`;
    }
    
    // Create Tags section
    if (groups.tag.length > 0) {
      twee += `:: _Tags\n`;
      twee += `<!--@url: [tags-index]-->\n`;
      twee += `<!--@nodeLevel: section-->\n`;
      twee += `<!--@displayName: Tags-->\n`;
      twee += `\n`;
      groups.tag.forEach((node) => {
        const headlines = headlineData[node.url];
        const linkName = headlines?.h1?.text || headlines?.title || 
          node.name.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        const tagId = getUniquePassageName(sanitizePassageName(linkName));
        twee += `[[${linkName}|${tagId}]]\n`;
        
        const { passageContent } = createPageWithChildren(node, tagId, false);
        const modifiedContent = passageContent.replace('[[Back to Home|Start]]', '[[Back to Tags|_Tags]]');
        allPassages.push({
          id: tagId,
          content: modifiedContent
        });
      });
      twee += `[[Back to Home|Start]]\n\n`;
    }
    
    // Create Categories section
    if (groups.category.length > 0) {
      twee += `:: _Categories\n`;
      twee += `<!--@url: [categories-index]-->\n`;
      twee += `<!--@nodeLevel: section-->\n`;
      twee += `<!--@displayName: Categories-->\n`;
      twee += `\n`;
      groups.category.forEach((node) => {
        const headlines = headlineData[node.url];
        const linkName = headlines?.h1?.text || headlines?.title || 
          node.name.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        const catId = getUniquePassageName(sanitizePassageName(linkName));
        twee += `[[${linkName}|${catId}]]\n`;
        
        const { passageContent } = createPageWithChildren(node, catId, false);
        const modifiedContent = passageContent.replace('[[Back to Home|Start]]', '[[Back to Categories|_Categories]]');
        allPassages.push({
          id: catId,
          content: modifiedContent
        });
      });
      twee += `[[Back to Home|Start]]\n\n`;
    }
    
    return twee;
  };

  // Handle URL fetch
  const handleFetchSitemap = async () => {
    if (!websiteUrl.trim()) {
      toast({ title: 'Error', description: 'Please enter a website URL', variant: 'destructive' });
      return;
    }
    
    // Save URL to history before fetching
    saveUrlToHistory(websiteUrl);
    
    setIsLoading(true);
    setProgress({ current: 0, total: 0, status: 'Fetching sitemap...' });
    
    try {
      // Try to fetch sitemap
      const response = await fetch(`${API_BASE_URL}/api/sitemap/fetch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: websiteUrl })
      });
      
      if (!response.ok) {
        const error = await response.json();
        const detail = error.detail;
        
        // Check if it's a bot protection error with sitemap URL
        if (typeof detail === 'object' && detail.sitemap_url) {
          // Show persistent toast with clickable link
          toast({
            title: detail.message,
            description: (
              <div className="mt-2">
                <p className="mb-2">{detail.action}</p>
                <a 
                  href={detail.sitemap_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 underline font-medium"
                >
                  Open Sitemap → {detail.sitemap_url}
                </a>
              </div>
            ),
            variant: 'destructive',
            duration: Infinity, // Stay until closed
          });
        } else {
          toast({ 
            title: 'Error', 
            description: typeof detail === 'string' ? detail : 'Failed to fetch sitemap', 
            variant: 'destructive' 
          });
        }
        setIsLoading(false);
        return;
      }
      
      const data = await response.json();
      processContent(data.content, websiteUrl);
      
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
      setIsLoading(false);
    }
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadedFile(file);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        processContent(event.target.result, file.name);
      } catch (error) {
        toast({ title: 'Error', description: error.message, variant: 'destructive' });
      }
    };
    reader.readAsText(file);
  };

  // Handle paste submission
  const handlePasteSubmit = () => {
    if (!pastedContent.trim()) {
      toast({ title: 'Error', description: 'Please paste sitemap content', variant: 'destructive' });
      return;
    }
    processContent(pastedContent, 'Pasted Sitemap');
  };

  // Fetch headlines from page URLs
  const fetchHeadlinesFromUrls = async (urls) => {
    setIsFetchingHeadlines(true);
    setProgress({ current: 0, total: urls.length, status: 'Fetching page headlines...' });
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/sitemap/fetch-headlines`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ urls })
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch headlines');
      }
      
      const data = await response.json();
      
      // Convert results array to a lookup object by URL
      const headlineData = {};
      data.results.forEach(result => {
        headlineData[result.url] = {
          title: result.title,
          h1: result.h1,
          h2s: result.h2s || []
        };
      });
      
      setProgress({ 
        current: data.successful, 
        total: data.total_urls, 
        status: `Fetched headlines from ${data.successful}/${data.total_urls} pages` 
      });
      
      return headlineData;
    } catch (error) {
      console.error('Error fetching headlines:', error);
      toast({ 
        title: 'Warning', 
        description: 'Could not fetch page headlines. Using URL paths instead.',
        variant: 'default' 
      });
      return {};
    } finally {
      setIsFetchingHeadlines(false);
    }
  };

  // Process sitemap content
  const processContent = async (content, source) => {
    setIsLoading(true);
    setProgress({ current: 0, total: 0, status: 'Parsing sitemap...' });
    
    try {
      const urlData = parseSitemapXml(content);
      setProgress({ current: 1, total: 4, status: `Found ${urlData.length} URLs` });
      
      const hierarchy = buildHierarchy(urlData);
      setProgress({ current: 2, total: 4, status: 'Building hierarchy...' });
      
      // Extract domain for title (handle both string URLs and {url, contentType} objects)
      let siteTitle = 'Site Navigation';
      try {
        const firstUrlStr = typeof urlData[0] === 'string' ? urlData[0] : urlData[0].url;
        const firstUrl = new URL(firstUrlStr);
        siteTitle = firstUrl.hostname;
      } catch (e) {
        // Ignore URL parsing errors
      }
      
      // Fetch headlines if enabled (extract just the URL strings)
      let headlineData = {};
      if (includeH1 || includeH2) {
        const urlStrings = urlData.map(u => typeof u === 'string' ? u : u.url);
        setProgress({ current: 3, total: 4, status: `Fetching headlines from ${urlStrings.length} pages...` });
        headlineData = await fetchHeadlinesFromUrls(urlStrings);
      }
      
      const twee = convertToTwee(hierarchy.nodes, siteTitle, headlineData, {
        includeH1Nodes: includeH1,
        includeH2Nodes: includeH2
      });
      setProgress({ current: 4, total: 4, status: 'Complete!' });
      
      setParsedData({
        urls: urlData.length,
        nodes: hierarchy.nodes.length,
        twee: twee,
        source: source,
        headlinesLoaded: Object.keys(headlineData).length > 0
      });
      
      const headlineStatus = (includeH1 || includeH2)
        ? ` (with ${Object.keys(headlineData).length} page headlines)` 
        : '';
      setValidationResult({ 
        success: true, 
        message: `Successfully parsed ${urlData.length} URLs${headlineStatus}` 
      });
      setIsLoading(false);
      
    } catch (error) {
      setValidationResult({ success: false, message: error.message });
      setIsLoading(false);
    }
  };

  // Send to compiler
  const handleSendToCompiler = () => {
    if (!parsedData?.twee) {
      toast({ title: 'Error', description: 'No data to send', variant: 'destructive' });
      return;
    }
    
    // Store twee in localStorage for compiler to pick up
    localStorage.setItem('compiler_twee_code', parsedData.twee);
    localStorage.setItem('compiler_action', 'import');
    
    toast({ title: 'Success', description: 'Redirecting to compiler...' });
    navigate('/compiler');
  };

  // Send to Visual Editor
  const handleSendToVisualEditor = () => {
    if (!parsedData?.twee) {
      toast({ title: 'Error', description: 'No data to send', variant: 'destructive' });
      return;
    }
    
    // Store twee in sessionStorage for Visual Editor to pick up
    sessionStorage.setItem('imported_twee', parsedData.twee);
    sessionStorage.setItem('imported_twee_auto', 'true'); // Auto-generate map
    
    toast({ title: 'Success', description: 'Redirecting to Visual Editor...' });
    navigate('/visual-editor');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO 
        title="Sitemap to PathNav - TweePorter"
        description="Convert your website sitemap into an interactive mind map navigation."
      />
      <PageHeader borderBottom={true} />
      
      {/* Action Bar */}
      <div className="bg-white border-b border-slate-200 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Globe className="w-5 h-5 text-cyan-600" />
          <span className="font-semibold text-slate-800">Sitemap to PathNav</span>
          <span className="text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded-full">Beta</span>
          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Agency</span>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Import Your Sitemap</h1>
          <p className="text-slate-600 mb-6">
            Convert your website's sitemap into an interactive mind map navigation. 
            Provide your sitemap in one of three ways:
          </p>
          
          {/* Input Mode Tabs */}
          <div className="flex gap-2 mb-6">
            <Button
              variant={inputMode === 'url' ? 'default' : 'outline'}
              onClick={() => setInputMode('url')}
              className={inputMode === 'url' ? 'bg-cyan-600 hover:bg-cyan-700' : ''}
            >
              <Globe className="w-4 h-4 mr-2" />
              Scrape URL
            </Button>
            <Button
              variant={inputMode === 'upload' ? 'default' : 'outline'}
              onClick={() => setInputMode('upload')}
              className={inputMode === 'upload' ? 'bg-cyan-600 hover:bg-cyan-700' : ''}
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload File
            </Button>
            <Button
              variant={inputMode === 'paste' ? 'default' : 'outline'}
              onClick={() => setInputMode('paste')}
              className={inputMode === 'paste' ? 'bg-cyan-600 hover:bg-cyan-700' : ''}
            >
              <FileText className="w-4 h-4 mr-2" />
              Paste XML
            </Button>
          </div>
          
          {/* Options */}
          <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Include Page Sections as Child Nodes</h3>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="include-h1" 
                  checked={includeH1}
                  onCheckedChange={setIncludeH1}
                />
                <label 
                  htmlFor="include-h1" 
                  className="text-sm font-medium text-slate-700 cursor-pointer"
                >
                  Include H1 headlines as child nodes
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="include-h2" 
                  checked={includeH2}
                  onCheckedChange={setIncludeH2}
                />
                <label 
                  htmlFor="include-h2" 
                  className="text-sm font-medium text-slate-700 cursor-pointer"
                >
                  Include H2 headlines as child nodes
                </label>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Creates child nodes for H1/H2 headlines with direct URLs to those sections (with #anchors).
              May take longer for large sitemaps.
            </p>
          </div>
          
          {/* URL Input */}
          {inputMode === 'url' && (
            <div className="space-y-4">
              <div>
                <Label>Website URL</Label>
                <div className="flex gap-2 mt-1">
                  <div className="flex-1 relative">
                    <Input
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                      onFocus={() => urlHistory.length > 0 && setShowUrlDropdown(true)}
                      onBlur={() => setTimeout(() => setShowUrlDropdown(false), 200)}
                      placeholder="https://example.com"
                      className="w-full pr-8"
                    />
                    {urlHistory.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowUrlDropdown(!showUrlDropdown)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    )}
                    
                    {/* URL History Dropdown */}
                    {showUrlDropdown && urlHistory.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
                        <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 flex items-center gap-1.5">
                          <Clock className="w-3 h-3 text-slate-400" />
                          <span className="text-xs text-slate-500 font-medium">Recent URLs</span>
                        </div>
                        {urlHistory.map((url, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between px-3 py-2 hover:bg-cyan-50 cursor-pointer group"
                            onClick={() => {
                              setWebsiteUrl(url);
                              setShowUrlDropdown(false);
                            }}
                          >
                            <span className="text-sm text-slate-700 truncate flex-1">{url}</span>
                            <button
                              onClick={(e) => removeUrlFromHistory(url, e)}
                              className="ml-2 p-1 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Remove from history"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button 
                    onClick={handleFetchSitemap}
                    disabled={isLoading}
                    className="bg-cyan-600 hover:bg-cyan-700"
                  >
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Fetch Sitemap'}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  We'll try to find sitemap.xml at your domain
                </p>
              </div>
            </div>
          )}
          
          {/* File Upload */}
          {inputMode === 'upload' && (
            <div className="space-y-4">
              <div>
                <Label>Upload Sitemap File</Label>
                <div className="mt-1 border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                  <input
                    type="file"
                    accept=".xml,.txt"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="sitemap-upload"
                  />
                  <label htmlFor="sitemap-upload" className="cursor-pointer">
                    <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                    <p className="text-slate-600">Click to upload sitemap.xml</p>
                    <p className="text-xs text-slate-500 mt-1">XML format</p>
                  </label>
                  {uploadedFile && (
                    <p className="text-sm text-cyan-600 mt-2">Uploaded: {uploadedFile.name}</p>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Paste Content */}
          {inputMode === 'paste' && (
            <div className="space-y-4">
              <div>
                <Label>Paste Sitemap XML</Label>
                <Textarea
                  value={pastedContent}
                  onChange={(e) => setPastedContent(e.target.value)}
                  placeholder='<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
  <url><loc>https://example.com/</loc></url>
  <url><loc>https://example.com/about</loc></url>
</urlset>'
                  className="mt-1 font-mono text-sm min-h-[200px]"
                />
                <Button 
                  onClick={handlePasteSubmit}
                  disabled={isLoading}
                  className="mt-2 bg-cyan-600 hover:bg-cyan-700"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Parse Sitemap
                </Button>
              </div>
            </div>
          )}
          
          {/* Progress */}
          {isLoading && (
            <div className="mt-6 p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Loader2 className="w-4 h-4 animate-spin text-cyan-600" />
                <span className="text-sm text-slate-600">{progress.status}</span>
              </div>
              {progress.total > 0 && (
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div 
                    className="bg-cyan-600 h-2 rounded-full transition-all"
                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                  />
                </div>
              )}
            </div>
          )}
          
          {/* Validation Result */}
          {validationResult && (
            <div className={`mt-6 p-4 rounded-lg flex items-start gap-3 ${
              validationResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            }`}>
              {validationResult.success ? (
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p className={validationResult.success ? 'text-green-800' : 'text-red-800'}>
                  {validationResult.message}
                </p>
                {parsedData && (
                  <p className="text-sm text-slate-600 mt-1">
                    {parsedData.nodes} pages ready to convert to PathNav
                  </p>
                )}
              </div>
            </div>
          )}
          
          {/* Send to Compiler or Visual Editor */}
          {parsedData && validationResult?.success && (
            <div className="mt-6 flex justify-end gap-3">
              <Button 
                onClick={handleSendToVisualEditor}
                size="lg"
                variant="outline"
                className="border-purple-400 text-purple-600 hover:bg-purple-50"
              >
                Open in Visual Editor
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button 
                onClick={handleSendToCompiler}
                size="lg"
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
              >
                Open in Compiler
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
          
        </div>
      </div>
    </div>
  );
};

export default SitemapImporter;
